<template>
  <!-- Initial Loading Screen - Shows immediately on app start -->
  <div v-if="isInitializing" class="fixed inset-0 z-50 bg-gray-900 flex items-center justify-center">
    <div class="text-center">
      <img
        v-if="whiteLabelConfig?.theme?.logo?.default"
        :src="whiteLabelConfig.theme.logo.default"
        :alt="whiteLabelConfig?.branding?.companyName"
        class="w-20 h-20 mx-auto mb-8 object-contain"
      />
      <img
        v-else
        src="./assets/Tint_Bolt_Logo.png"
        alt="Tint Bolt Logo"
        class="w-20 h-20 mx-auto mb-8 object-contain"
      />
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-yellow-500 mx-auto"></div>
      <p class="mt-4 text-gray-400">Initializing...</p>
    </div>
  </div>

  <!-- Main App Content -->
  <div v-else>
    <BlankLoader />
    <router-view />
        <TutorialPlaylist />
    <IntegrationSlideOver v-if="isLocalhost" />
    <SatisfactionSurvey
      v-if="satisfactionSurveyOpen && !updatesBlocking"
      :is-open="satisfactionSurveyOpen" 
      @close="closeSatisfactionSurvey"
    />
    <MarketingPermissionPopup
      v-if="marketingPermissionOpen && !updatesBlocking"
      :is-open="marketingPermissionOpen" 
      @close="closeMarketingPermission"
    />
    <FeedbackModal
      v-if="store.state.feedbackModalOpen && !updatesBlocking"
      :is-open="store.state.feedbackModalOpen" 
      :prefill="store.state.feedbackPrefill"
      @close="closeFeedbackModal"
    />
  </div>
</template>

<script setup>
import BlankLoader from "./components/loadingScreen/blankLoader.vue";
import Footer from "./components/Footer.vue";
import useWhiteLabel from './composables/useWhiteLabel';
import useSecurityProtocols from './composables/useSecurityProtocols'
import { onMounted, watch, ref, provide, computed, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from "vue-router";
import { projectAuth } from "./firebase/config";
import { useSatisfactionSurvey } from './composables/useSatisfactionSurvey';
import { useMarketingPermission } from './composables/useMarketingPermission';
import TutorialPlaylist from './components/Modals/TutorialPlaylist.vue';
import IntegrationSlideOver from './components/BackOffice/Integrations/IntegrationSlideOver.vue';
const SatisfactionSurvey = defineAsyncComponent(() => import(
  /* webpackChunkName: "modal-satisfaction-survey" */
  './components/Modals/SatisfactionSurvey.vue'
));
const MarketingPermissionPopup = defineAsyncComponent(() => import(
  /* webpackChunkName: "modal-marketing-permission" */
  './components/Modals/MarketingPermissionPopup.vue'
));
const FeedbackModal = defineAsyncComponent(() => import(
  /* webpackChunkName: "modal-feedback" */
  './components/Modals/FeedbackModal.vue'
));
const store = useStore();
const route = useRoute();

// The update inbox owns release announcements, including the earlier PPF post.
// Wait for its history check before letting other account popups take focus.
const updatesBlocking = computed(() => route.name === 'Dashboard' && !!store.state.currentUser?.uid &&
  (store.state.updateSettings.isUpdatePopupOpen || store.state.updateSettings.updatesChecked !== true));
const isInitializing = ref(true);
const closeFeedbackModal = () => {
  store.state.feedbackModalOpen = false;
  store.state.feedbackPrefill = null;
};

// Satisfaction survey
const { surveyOpen, showSurveyIfNeeded, refreshCompletionStatus } = useSatisfactionSurvey();
const satisfactionSurveyOpen = computed(() => store.state.satisfactionSurveyOpen || surveyOpen.value);
const closeSatisfactionSurvey = async () => {
  surveyOpen.value = false;
  store.state.satisfactionSurveyOpen = false;
  // Refresh completion status after closing
  await refreshCompletionStatus();
};

// Marketing permission popup
const { popupOpen, showPopupIfNeeded, refreshCompletionStatus: refreshMarketingPermission } = useMarketingPermission();
const marketingPermissionOpen = computed(() => popupOpen.value);
const closeMarketingPermission = async () => {
  popupOpen.value = false;
  // Refresh completion status after closing
  await refreshMarketingPermission();
};

const { whiteLabelConfig, isLoading } = useWhiteLabel();
const { fetchSecuritySettings } = useSecurityProtocols()

// Computed property to determine if we're in development environment
const isInDevelopment = computed(() => {
  return window.location.hostname === "localhost" || 
         window.location.hostname.includes("dev") ||
         window.location.hostname.includes("staging");
});

// Computed property to check if we're specifically on localhost
const isLocalhost = computed(() => {
  return window.location.hostname === "localhost";
});

onMounted(async () => {
  try {
    // Fetch initial security settings
    if (store.state.currentUser) {
      try {
        await fetchSecuritySettings(true) // Force initial fetch
      } catch (error) {
        console.warn('Failed to fetch security settings:', error);
      }
    }
    
    // Wait for white label config to load (with timeout fallback)
    const isDashboardPath = window.location.pathname.startsWith("/dashboard");
    const whiteLabelTimeout =
      isDashboardPath && projectAuth.currentUser ? 1500 : 5000;

    if (isLoading.value) {
      await Promise.race([
        new Promise(resolve => {
          // Check immediately in case it's already false
          if (!isLoading.value) {
            resolve();
            return;
          }
          
          const unwatch = watch(isLoading, (newValue) => {
            if (!newValue) {
              unwatch();
              resolve();
            }
          });
        }),
        new Promise(resolve => setTimeout(resolve, whiteLabelTimeout))
      ]);
    }
    
  } catch (error) {
    console.error('Error during initialization:', error);
  } finally {
    // Always hide initial loading screen, even if there were errors
    isInitializing.value = false;
  }
})

// Refresh security settings when the signed-in user changes.
watch(() => store.state.currentUser, async (newUser) => {
  if (newUser) {
    await fetchSecuritySettings(true);
  }
}, { immediate: true })

</script>
